(function() {
  'use strict';

  class BundleBuilder {
    constructor(el) {
      this.el = el;
      this.mainProductId = el.dataset.mainProductId;
      this.discountCode = el.dataset.discountCode || '';
      this.rows = Array.from(el.querySelectorAll('[data-bundle-row]'));
      this.selections = {};
      this.mainForm = null;
      this.init();
    }

    init() {
      this.rows.forEach(row => this.initRow(row));
      this.findAndHookMainForm();
      this.updateState();
    }

    findAndHookMainForm() {
      const forms = document.querySelectorAll('form[action*="/cart/add"]');
      this.mainForm = Array.from(forms).find(f => !this.el.contains(f));

      if (!this.mainForm) {
        console.warn('[BundleBuilder] Could not find main product form.');
        return;
      }

      this.mainForm.addEventListener('submit', (e) => this.handleMainFormSubmit(e), true);
    }

    async handleMainFormSubmit(e) {
      const bundleItems = this.buildBundleItems();
      if (bundleItems.length === 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const formData = new FormData(this.mainForm);
      const mainVariantId = formData.get('id');
      const mainQty = parseInt(formData.get('quantity') || '1', 10);

      if (!mainVariantId) {
        this.mainForm.submit();
        return;
      }

      const mainProperties = {};
      formData.forEach((value, key) => {
        if (key.startsWith('properties[') && key.endsWith(']')) {
          const propName = key.slice(11, -1);
          if (value) mainProperties[propName] = value;
        }
      });

      const items = [
        {
          id: parseInt(mainVariantId, 10),
          quantity: mainQty,
          ...(Object.keys(mainProperties).length ? { properties: mainProperties } : {})
        },
        ...bundleItems
      ];

      const submitBtn = this.mainForm.querySelector('[type="submit"], [name="add"]');
      let originalBtnHTML = null;
      if (submitBtn) {
        originalBtnHTML = submitBtn.innerHTML;
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }

      const hasGift = bundleItems.some(i => i.properties && i.properties._bundle_gift === 'true');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || errData.message || 'Could not add bundle');
        }

        if (hasGift && this.discountCode) {
          window.location.href = `/discount/${encodeURIComponent(this.discountCode)}?redirect=/cart`;
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));

        setTimeout(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
            submitBtn.innerHTML = originalBtnHTML;
          }
        }, 800);

      } catch (err) {
        console.error('[BundleBuilder] Add error:', err);
        alert(err.message || 'Could not add bundle. Please try again.');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
          submitBtn.innerHTML = originalBtnHTML;
        }
      }
    }

    buildBundleItems() {
      const items = [];
      Object.values(this.selections).forEach(sel => {
        if (!sel.variantId) return;
        const item = {
          id: parseInt(sel.variantId, 10),
          quantity: 1,
          properties: sel.isGift
            ? { _bundle_gift: 'true' }
            : { _bundle_item: 'true' }
        };
        items.push(item);
      });
      return items;
    }

    initRow(row) {
      const productBtns = row.querySelectorAll('[data-bundle-product]');
      const variantsContainer = row.querySelector('[data-bundle-variants]');

      let firstAvailable = null;
      productBtns.forEach(btn => {
        const pid = btn.dataset.productId;
        const variantsBox = variantsContainer ? variantsContainer.querySelector(`[data-variants-for="${pid}"]`) : null;
        const hasAvailable = variantsBox && (
          variantsBox.querySelector('[data-bundle-variant][data-available="true"]') ||
          variantsBox.querySelector('[data-auto-select="true"][data-available="true"]')
        );
        if (hasAvailable && !firstAvailable) firstAvailable = btn;
      });

      if (firstAvailable) {
        const isLocked = row.classList.contains('bundle-row--locked');
        this.selectProduct(row, firstAvailable, isLocked);
      }

      productBtns.forEach(btn => {
        btn.addEventListener('click', e => {
          e.preventDefault();
          if (row.classList.contains('bundle-row--locked')) return;
          this.selectProduct(row, btn);
        });
      });

      if (variantsContainer) {
        variantsContainer.addEventListener('click', e => {
          const variantBtn = e.target.closest('[data-bundle-variant]');
          if (!variantBtn || variantBtn.disabled) return;
          if (row.classList.contains('bundle-row--locked')) return;
          this.selectVariant(row, variantBtn);
        });
      }
    }

    selectProduct(row, productBtn, lockedPreview = false) {
      const rowId = row.dataset.rowId;
      const productId = productBtn.dataset.productId;
      const productTitle = productBtn.dataset.productTitle;

      row.querySelectorAll('[data-bundle-product]').forEach(b => b.classList.remove('is-active'));
      productBtn.classList.add('is-active');

      const variantsContainer = row.querySelector('[data-bundle-variants]');
      if (variantsContainer) {
        variantsContainer.querySelectorAll('.bundle-variants').forEach(v => v.classList.remove('is-active'));
        const activeVariants = variantsContainer.querySelector(`[data-variants-for="${productId}"]`);
        if (activeVariants) {
          activeVariants.classList.add('is-active');

          const autoSelectInput = activeVariants.querySelector('[data-auto-select="true"]');
          if (autoSelectInput) {
            if (!lockedPreview) {
              this.selections[rowId] = {
                productId,
                productTitle,
                variantId: autoSelectInput.dataset.variantId,
                variantTitle: autoSelectInput.dataset.variantTitle,
                isGift: row.dataset.isGift === 'true'
              };
              this.updateRowDisplay(row, '');
            }
          } else {
            const firstAvailable = activeVariants.querySelector('[data-bundle-variant][data-available="true"]:not(.is-disabled)');
            if (firstAvailable) {
              if (lockedPreview) {
                firstAvailable.classList.add('is-active');
                this.updateRowDisplay(row, firstAvailable.dataset.variantTitle);
              } else {
                this.selectVariant(row, firstAvailable);
              }
            } else {
              if (!lockedPreview) delete this.selections[rowId];
              this.updateRowDisplay(row, '');
            }
          }
        }
      }

      if (!lockedPreview) this.updateState();
    }

    selectVariant(row, variantBtn) {
      const rowId = row.dataset.rowId;
      const productId = variantBtn.dataset.productId;
      const productBtn = row.querySelector(`[data-bundle-product][data-product-id="${productId}"]`);
      const productTitle = productBtn ? productBtn.dataset.productTitle : '';

      const variantsBox = variantBtn.closest('.bundle-variants');
      if (variantsBox) {
        variantsBox.querySelectorAll('[data-bundle-variant]').forEach(b => b.classList.remove('is-active'));
      }
      variantBtn.classList.add('is-active');

      this.selections[rowId] = {
        productId,
        productTitle,
        variantId: variantBtn.dataset.variantId,
        variantTitle: variantBtn.dataset.variantTitle,
        isGift: row.dataset.isGift === 'true'
      };

      this.updateRowDisplay(row, variantBtn.dataset.variantTitle);
      this.updateState();
    }

    updateRowDisplay(row, variantTitle) {
      const nameEl = row.querySelector('[data-selected-name]');
      if (nameEl) {
        nameEl.textContent = variantTitle && variantTitle !== 'Default' ? variantTitle : '';
      }
    }

    updateState() {
      const nonGiftRows = this.rows.filter(r => r.dataset.isGift !== 'true');
      const giftRows = this.rows.filter(r => r.dataset.isGift === 'true');

      const allNonGiftSelected = nonGiftRows.every(r => this.selections[r.dataset.rowId]);

      giftRows.forEach(giftRow => {
        if (allNonGiftSelected) {
          giftRow.classList.remove('bundle-row--locked');
          if (!this.selections[giftRow.dataset.rowId]) {
            const firstProduct = giftRow.querySelector('[data-bundle-product]');
            if (firstProduct) this.selectProduct(giftRow, firstProduct);
          }
        } else {
          giftRow.classList.add('bundle-row--locked');
          delete this.selections[giftRow.dataset.rowId];
        }
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-bundle-builder]').forEach(el => {
      if (!el.dataset.bundleInit) {
        el.dataset.bundleInit = 'true';
        new BundleBuilder(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();